import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Lideranca } from '@eleg360/types';
import { liderancasApi } from '@/lib/api/liderancas';
import { contatosApi } from '@/lib/api/contatos';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { useMockStore } from '@/lib/stores/mockStore';
import { toast } from '@/lib/stores/toastStore';
import { Drawer } from '@/design-system/Drawer';
import { formatarData } from '@/lib/format';
import { exportarCsv } from '@/lib/csv';

const TIPOS = ['Comunitária', 'Religiosa', 'Sindical', 'Empresarial', 'Cultural', 'Esportiva'];

/** Saúde do relacionamento: 0-100, informada pela equipe. */
const corDaSaude = (s?: number) =>
  s === undefined || s === null
    ? 'neutral'
    : s >= 70
    ? 'good'
    : s >= 40
    ? 'warn'
    : 'crit';

type FormLideranca = {
  nome: string;
  tipo: string;
  bairro: string;
  area: string;
  saude: number;
  status: 'ativa' | 'inativa';
};

const FORM_VAZIO: FormLideranca = {
  nome: '',
  tipo: 'Comunitária',
  bairro: '',
  area: '',
  saude: 100,
  status: 'ativa',
};

export function LiderancasPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const { bairros } = useBairros();
  const setBairroActiveId = useMockStore((s) => s.setBairroActiveId);

  const [searchParams, setSearchParams] = useSearchParams();

  const [q, setQ] = useState('');
  const [bairroFiltro, setBairroFiltro] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [statusFiltro, setStatusFiltro] = useState<'todos' | 'ativa' | 'inativa'>('todos');

  const [selecionada, setSelecionada] = useState<Lideranca | null>(null);
  const [modal, setModal] = useState<'nova' | 'editar' | null>(null);
  const [form, setForm] = useState<FormLideranca>(FORM_VAZIO);

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const filtros = {
    search: q || undefined,
    bairro: bairroFiltro || undefined,
    tipo: tipoFiltro || undefined,
  };

  const { data: todas = [], isLoading, isError, error } = useQuery({
    queryKey: ['liderancas', filtros],
    queryFn: () => liderancasApi.list(filtros, token),
    enabled: !!token,
  });

  /**
   * Deep-link da paleta de comandos: `/liderancas?lideranca=<id>` abre a ficha,
   * na mesma convenção de `/contatos?contato=<id>` (Task 5.2). A lista da tela
   * é filtrada, então quando a liderança não está nela ela é buscada pelo id.
   */
  const liderancaAlvo = searchParams.get('lideranca') ?? '';
  const liderNaLista = todas.find((l) => l.id === liderancaAlvo);

  const { data: liderPorLink } = useQuery({
    queryKey: ['liderancas', 'link', liderancaAlvo],
    queryFn: () => liderancasApi.get(liderancaAlvo, token),
    enabled: !!token && !!liderancaAlvo && !liderNaLista,
  });

  const liderDoLink = liderNaLista ?? liderPorLink;

  useEffect(() => {
    if (!liderancaAlvo || !liderDoLink) return;
    setSelecionada(liderDoLink);
    const p = new URLSearchParams(searchParams);
    p.delete('lideranca');
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liderancaAlvo, liderDoLink]);

  // Contatos vinculados a cada liderança, para o painel de cobertura
  const { data: contatosPagina } = useQuery({
    queryKey: ['contatos', 'liderancas'],
    queryFn: () => contatosApi.list({ limit: 500 }, token),
    enabled: !!token,
  });

  const contatos = contatosPagina?.items ?? [];

  const liderancas = todas.filter((l) => statusFiltro === 'todos' || l.status === statusFiltro);

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['liderancas'] });
    queryClient.invalidateQueries({ queryKey: ['bairros'] });
    queryClient.invalidateQueries({ queryKey: ['metas'] });
  };

  const criar = useMutation({
    mutationFn: (dados: Partial<Lideranca>) => liderancasApi.create(dados, token),
    onSuccess: () => {
      invalidar();
      toast('Liderança cadastrada.');
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao cadastrar a liderança.')),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<Lideranca> }) =>
      liderancasApi.update(id, dados, token),
    onSuccess: (l) => {
      invalidar();
      toast('Liderança atualizada.');
      setSelecionada(l);
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar a liderança.')),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => liderancasApi.delete(id, token),
    onSuccess: () => {
      invalidar();
      toast('Liderança removida.');
      setSelecionada(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover a liderança.')),
  });

  const ativas = todas.filter((l) => l.status === 'ativa');
  const tipos = [...new Set(todas.map((l) => l.tipo))];
  const contatosDe = (l: Lideranca) => contatos.filter((c) => c.lideranca_id === l.id);
  const semLideranca = bairros.filter(
    (b) => !ativas.some((l) => l.bairro?.toLowerCase() === b.n.toLowerCase()),
  );
  const saudeMedia = ativas.length
    ? Math.round(ativas.reduce((t, l) => t + (l.saude ?? 0), 0) / ativas.length)
    : 0;

  const abrirNova = () => {
    setForm({ ...FORM_VAZIO, bairro: bairros[0]?.n || '' });
    setModal('nova');
  };

  const abrirEdicao = (l: Lideranca) => {
    setForm({
      nome: l.nome,
      tipo: l.tipo,
      bairro: l.bairro || '',
      area: l.area || '',
      saude: l.saude ?? 100,
      status: l.status,
    });
    setSelecionada(l);
    setModal('editar');
  };

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim()) {
      toast('O nome da liderança é obrigatório.');
      return;
    }
    const dados: Partial<Lideranca> = {
      nome: form.nome.trim(),
      tipo: form.tipo,
      bairro: form.bairro || undefined,
      municipio: form.bairro ? 'Fortaleza' : undefined,
      uf: form.bairro ? 'CE' : undefined,
      area: form.area.trim() || undefined,
      saude: form.saude,
      status: form.status,
    };

    if (modal === 'nova') criar.mutate(dados);
    else if (selecionada) atualizar.mutate({ id: selecionada.id, dados });
  };

  const alternarStatus = (l: Lideranca) =>
    atualizar.mutate({
      id: l.id,
      dados: { status: l.status === 'ativa' ? 'inativa' : 'ativa' },
    });

  const irParaTerritorio = (nome?: string) => {
    const b = bairros.find((x) => x.n === nome);
    if (b) setBairroActiveId(b.id);
    navigate('/territorio');
  };

  return (
    <div className="page" id="p-liderancas">
      <div className="head">
        <div>
          <h1>Lideranças</h1>
          <p>
            Vínculo político-territorial. Um território pode ter várias lideranças; cada uma aponta
            para um ou mais contatos. A saúde do relacionamento é informada pelo gabinete, não
            calculada.
          </p>
        </div>
        <div className="act">
          <button
            className="btn"
            onClick={() =>
              exportarCsv(
                'liderancas',
                [
                  { cabecalho: 'Nome', valor: (l: any) => l.nome },
                  { cabecalho: 'Tipo', valor: (l: any) => l.tipo },
                  { cabecalho: 'Território', valor: (l: any) => l.bairro },
                  { cabecalho: 'Área', valor: (l: any) => l.area },
                  { cabecalho: 'Status', valor: (l: any) => l.status },
                  { cabecalho: 'Saúde do vínculo', valor: (l: any) => l.saude ?? '' },
                ],
                liderancas,
              )
            }
            disabled={liderancas.length === 0}
            title="Exporta as lideranças como estão filtradas na tela"
          >
            Exportar
          </button>
          <button className="btn pri" onClick={abrirNova}>
            Nova liderança
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Lideranças ativas</div>
          <div className="v">{ativas.length}</div>
          <div className="d">
            {todas.length} cadastradas · {tipos.length} tipo(s)
          </div>
        </div>
        <div className="card kpi">
          <div className="l">Contatos vinculados</div>
          <div className="v">{contatos.filter((c) => c.lideranca_id).length}</div>
          <div className="d">
            média de{' '}
            {(contatos.filter((c) => c.lideranca_id).length / (ativas.length || 1)).toFixed(1)} por
            liderança
          </div>
        </div>
        <div className="card kpi">
          <div className="l">Saúde média</div>
          <div className="v" style={{ color: saudeMedia >= 70 ? 'var(--good)' : 'var(--warn)' }}>
            {saudeMedia}
          </div>
          <div className="d">score de relacionamento das ativas</div>
        </div>
        <div className="card kpi">
          <div className="l">Territórios descobertos</div>
          <div className="v" style={{ color: semLideranca.length ? 'var(--crit)' : 'var(--good)' }}>
            {semLideranca.length}
          </div>
          <div className="d">sem nenhuma liderança ativa</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filters">
        <input
          className="in"
          style={{ width: 260 }}
          placeholder="Buscar por nome ou área..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className="sel" value={bairroFiltro} onChange={(e) => setBairroFiltro(e.target.value)}>
          <option value="">Todos os territórios</option>
          {bairros.map((b) => (
            <option key={b.id} value={b.n}>
              {b.n}
            </option>
          ))}
        </select>
        <select className="sel" value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
          <option value="">Todos os tipos</option>
          {TIPOS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <span style={{ flex: 1 }} />
        {(['todos', 'ativa', 'inativa'] as const).map((s) => (
          <span
            key={s}
            className={`chip neutral chipbtn ${statusFiltro === s ? 'on' : ''}`}
            onClick={() => setStatusFiltro(s)}
          >
            {s === 'todos' ? 'Todas' : s === 'ativa' ? 'Ativas' : 'Inativas'}
          </span>
        ))}
      </div>

      {/* Tabela */}
      <div className="card flat tw">
        <table>
          <thead>
            <tr>
              <th>Liderança</th>
              <th>Tipo</th>
              <th>Território</th>
              <th style={{ textAlign: 'center' }}>Contatos</th>
              <th>Saúde</th>
              <th>Situação</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {liderancas.map((l) => (
              <tr
                key={l.id}
                className="row"
                style={{ cursor: 'pointer', opacity: l.status === 'ativa' ? 1 : 0.6 }}
                onClick={() => setSelecionada(l)}
              >
                <td>
                  <b>{l.nome}</b>
                  {l.area && (
                    <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{l.area}</div>
                  )}
                </td>
                <td>
                  <span className="chip neutral">{l.tipo}</span>
                </td>
                <td>{l.bairro || '—'}</td>
                <td className="num" style={{ textAlign: 'center' }}>
                  {contatosDe(l).length}
                </td>
                <td>
                  <span className={`chip ${corDaSaude(l.saude)}`}>{l.saude ?? '—'}</span>
                </td>
                <td>
                  <span className={`chip ${l.status === 'ativa' ? 'good' : 'neutral'}`}>
                    {l.status}
                  </span>
                </td>
                <td style={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                  <button className="btn sm" onClick={() => abrirEdicao(l)}>
                    ✏️
                  </button>{' '}
                  <button
                    className="btn sm"
                    disabled={atualizar.isPending}
                    onClick={() => alternarStatus(l)}
                  >
                    {l.status === 'ativa' ? 'Inativar' : 'Reativar'}
                  </button>
                </td>
              </tr>
            ))}

            {isLoading && (
              <tr>
                <td colSpan={7} className="empty">
                  Carregando lideranças...
                </td>
              </tr>
            )}
            {isError && !isLoading && (
              <tr>
                <td colSpan={7} className="empty">
                  Não foi possível carregar: {erroDe(error, 'erro de comunicação com a API.')}
                </td>
              </tr>
            )}
            {!isLoading && !isError && liderancas.length === 0 && (
              <tr>
                <td colSpan={7} className="empty">
                  Nenhuma liderança com os filtros aplicados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Territórios sem liderança */}
      {semLideranca.length > 0 && (
        <div className="card" style={{ marginTop: 12, borderLeft: '4px solid var(--warn)' }}>
          <h2 style={{ fontSize: 14, margin: 0 }}>Territórios sem liderança ativa</h2>
          <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '4px 0 10px' }}>
            Articulação descoberta: nenhuma liderança responde por estes territórios.
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {semLideranca.map((b) => (
              <span
                key={b.id}
                className="chip warn chipbtn"
                style={{ cursor: 'pointer' }}
                onClick={() => irParaTerritorio(b.n)}
                title={`Necessidade ${b.nec}/100`}
              >
                {b.n} · nec {b.nec}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Drawer: ficha */}
      {selecionada && !modal && (
        <Drawer
          aberto
          titulo={selecionada.nome}
          aoFechar={() => setSelecionada(null)}
        >
          <div className="filters" style={{ marginBottom: 12 }}>
            <span className="chip neutral">{selecionada.tipo}</span>
            {selecionada.bairro && <span className="chip eleg">{selecionada.bairro}</span>}
            <span className={`chip ${corDaSaude(selecionada.saude)}`}>
              saúde {selecionada.saude ?? '—'}
            </span>
            <span className={`chip ${selecionada.status === 'ativa' ? 'good' : 'neutral'}`}>
              {selecionada.status}
            </span>
          </div>

          <dl className="dl">
            <dt>Área de atuação</dt>
            <dd>{selecionada.area || '—'}</dd>
            <dt>Território</dt>
            <dd>
              {selecionada.bairro ? (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    irParaTerritorio(selecionada.bairro);
                  }}
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  {selecionada.bairro}
                </a>
              ) : (
                '—'
              )}
            </dd>
            <dt>Cadastrada em</dt>
            <dd>{formatarData(selecionada.created_at)}</dd>
            <dt>Última alteração</dt>
            <dd>{formatarData(selecionada.updated_at)}</dd>
          </dl>

          <h3 style={{ marginTop: 16 }}>
            Contatos vinculados ({contatosDe(selecionada).length})
          </h3>
          <div style={{ maxHeight: 260, overflowY: 'auto', marginTop: 8 }}>
            {contatosDe(selecionada).map((c) => (
              <div
                key={c.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '6px 0',
                  borderBottom: '1px solid var(--line)',
                  fontSize: 12,
                }}
              >
                <span>
                  <b>{c.nome}</b>
                  <div style={{ color: 'var(--ink3)' }}>{c.bairro}</div>
                </span>
                <span className={`chip ${c.base_legal === 'optin' ? 'good' : 'warn'}`}>
                  {c.base_legal}
                </span>
              </div>
            ))}
            {contatosDe(selecionada).length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--ink3)' }}>
                Nenhum contato vinculado a esta liderança ainda.
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            <button className="btn" onClick={() => abrirEdicao(selecionada)}>
              ✏️ Editar
            </button>
            <button className="btn" onClick={() => navigate('/contatos')}>
              Ver contatos
            </button>
            <button
              className="btn"
              style={{ color: 'var(--crit)', marginLeft: 'auto' }}
              disabled={excluir.isPending}
              onClick={() => {
                if (window.confirm(`Remover a liderança "${selecionada.nome}"?`)) {
                  excluir.mutate(selecionada.id);
                }
              }}
            >
              Excluir
            </button>
          </div>
        </Drawer>
      )}

      {/* Modal: nova / editar */}
      {modal && (
        <form onSubmit={submeter}>
          <Drawer
            aberto
            titulo={modal === 'nova' ? 'Nova liderança' : 'Editar liderança'}
            aoFechar={() => setModal(null)}
            rodape={
              <>
                <button type="button" className="btn" onClick={() => setModal(null)}>
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn pri"
                  disabled={criar.isPending || atualizar.isPending}
                >
                  {criar.isPending || atualizar.isPending
                    ? 'Salvando...'
                    : modal === 'nova'
                    ? 'Criar liderança'
                    : 'Salvar alterações'}
                </button>
              </>
            }
          >
            <div className="field">
              <label>Nome *</label>
              <input
                className="in"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Nome da liderança"
                required
              />
            </div>

            <div className="grid g2">
              <div className="field">
                <label>Tipo</label>
                <select
                  className="in"
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                >
                  {TIPOS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Território</label>
                <select
                  className="in"
                  value={form.bairro}
                  onChange={(e) => setForm({ ...form, bairro: e.target.value })}
                >
                  <option value="">Não informado</option>
                  {bairros.map((b) => (
                    <option key={b.id} value={b.n}>
                      {b.n}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field">
              <label>Área de influência / associação</label>
              <input
                className="in"
                value={form.area}
                onChange={(e) => setForm({ ...form, area: e.target.value })}
                placeholder="Ex: Associação dos Moradores, Conselho Comunitário..."
              />
            </div>

            <div className="grid g2">
              <div className="field">
                <label>Saúde do relacionamento (0-100)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  className="in num"
                  value={form.saude}
                  onChange={(e) => setForm({ ...form, saude: Number(e.target.value) || 0 })}
                />
              </div>
              <div className="field">
                <label>Situação</label>
                <select
                  className="in"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value as 'ativa' | 'inativa' })}
                >
                  <option value="ativa">Ativa</option>
                  <option value="inativa">Inativa</option>
                </select>
              </div>
            </div>

          </Drawer>
        </form>
      )}
    </div>
  );
}
